import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, StyleSheet, Keyboard, Pressable } from "react-native";
import { useNavigation } from "@react-navigation/native";

import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import ToastMessage from "@components/Toast";

import t from "@constants/localization";
import color from "@constants/color";

import OTPInput from "./OTPInput";
import LinearGradient from "react-native-linear-gradient";
import LoadingModal from "@components/ModalLoader";
import { markTaskActions, resendRescheduleOtp } from "@services/api";
import useCountDown from "react-countdown-hook";
import OtpNumberModal from "@screens/RRD/OtpNumberModal";
import Text from "@components/CustomText";

interface TaskPayload {
  action: string;
  cx_id: number | string;
  status: string;
  pending_order_ids: Array<number | string>;
  reason: string;
  is_d_minus_2: boolean;
}

interface RouteParams {
  otpLength: number;
  otpText: string;
  phone_numbers: string[];
  taskPayload: TaskPayload;
  // How many screens to pop on success. The task-view flow pushes the OTP
  // screen over CustomerOrderView (pop 2 → back to the list); the call-modal
  // flow pushes it over the current screen (pop 1).
  popCount?: number;
}

interface Props {
  route: {
    params: RouteParams;
  };
}

const RescheduleOTPVerification: React.FC<Props> = ({ route }) => {
  const params = route.params || ({} as RouteParams);
  const {
    otpLength,
    otpText,
    phone_numbers = [],
    taskPayload,
    popCount = 1,
  } = params;

  const navigation = useNavigation();
  const [otp, setOtp] = useState("");
  const [otpMsgText, setOtpMsgText] = useState(otpText || "");
  const [isLoading, setIsLoading] = useState(false);
  const [countDown, { start, reset }] = useCountDown(30 * 1000, 1000);
  const [showResend, setShowResend] = useState(false);
  const [showNumberModal, setShowNumberModal] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("");
  const resendOtpBtnLoadingRef = useRef(false);
  const [otpError, setOtpError] = useState(false);

  const handleVerification = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await markTaskActions({ ...taskPayload, otp: `${otp}` });
      const payload = response?.data?.payload;
      if (payload?.error === "OTP_VERIFICATION_FAILED") {
        setOtpError(true);
        setOtpMsgText(payload?.errorText || otpText || "");
      } else {
        ToastMessage(
          "success",
          response?.data?.payload?.toastMessage || "Rescheduled",
        );
        navigation.pop(popCount);
      }
    } catch (error) {
      setOtpMsgText((error as Error)?.message || "");
      setOtpError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const isCountDownRunning = useMemo(() => countDown > 0, [countDown]);

  const resendOtp = async (phoneNumber: string): Promise<void> => {
    if (resendOtpBtnLoadingRef.current) return;
    resendOtpBtnLoadingRef.current = true;
    try {
      setSelectedPhoneNumber(phoneNumber);
      const response = await resendRescheduleOtp({
        cx_id: taskPayload.cx_id,
        action: taskPayload.action,
        pending_order_ids: taskPayload.pending_order_ids,
      });
      ToastMessage(
        "success",
        response?.data?.toastMessage || "OTP Sent Successfully",
        1500,
      );
      reset();
      start();
      setShowResend(true);
    } catch {
      ToastMessage("error", "Try Again", 1500);
    } finally {
      resendOtpBtnLoadingRef.current = false;
      setShowNumberModal(false);
    }
  };

  const handleOtpChange = (value: string): void => {
    if (otpError) {
      setOtpError(false);
      setOtpMsgText(otpText || "");
    }
    setOtp(value);
  };

  function resendOtpHelper() {
    if (!phone_numbers?.length) return null;
    if (phone_numbers?.length > 1) {
      setShowNumberModal(true);
      return;
    }
    resendOtp(phone_numbers[0]);
  }

  function PrimaryResendOTPLine() {
    return !showResend ? (
      <Pressable
        style={styles.resendOtpContainer}
        onPress={resendOtpHelper}
        testID="resendRescheduleOtpSingleNumber"
      >
        <Text
          newfont
          variant="body_text_4"
          style={styles.resendOtpText}
        >{`मैसेज के माध्यम से OTP भेजें `}</Text>
        <Text
          newfont
          variant="body_text_4"
          style={styles.phoneNumberText}
        >{`${phone_numbers?.length ? (phone_numbers?.length > 1 ? "" : `(फ़ोन : ${phone_numbers[0]})`) : ""}`}</Text>
      </Pressable>
    ) : (
      <View style={styles.centeredContainer}>
        <Text
          newfont
          variant="body_text_3"
        >{`OTP ${selectedPhoneNumber} पर भेजा गया`}</Text>
      </View>
    );
  }

  function SecondaryResendOTPLine() {
    return showResend ? (
      <View style={styles.rowCentered}>
        <Text newfont variant="body_text_3" style={styles.mediumBlackText}>
          OTP नहीं मिला?
        </Text>
        {isCountDownRunning ? (
          <Text newfont variant="body_text_2" style={styles.mediumBlackText}>
            दोबारा भेजें
          </Text>
        ) : null}
        <Pressable
          onPress={() => {
            if (isCountDownRunning) return;
            resendOtpHelper();
          }}
          testID="resendRescheduleOtpRetry"
        >
          <Text
            newfont
            variant="body_text_2"
            style={[
              styles.resendActionText,
              isCountDownRunning && styles.countdownText,
            ]}
          >
            {isCountDownRunning ? `${countDown / 1000} sec` : "दोबारा भेजें"}
          </Text>
        </Pressable>
        {isCountDownRunning ? (
          <Text newfont variant="body_text_2" style={styles.mediumBlackText}>
            में
          </Text>
        ) : null}
      </View>
    ) : null;
  }

  useEffect(() => {
    if (otp && otp.length === otpLength) {
      Keyboard.dismiss();
      handleVerification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle={t("screens/RRD/OTPVerification/header")}
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
      />
      <LinearGradient
        colors={["rgba(99, 99, 99, 1)", "rgba(18, 18, 18, 1)"]}
        start={{ x: 0.0, y: 0.1 }}
        end={{ x: 0.9, y: 0.4 }}
        style={styles.innerContainer}
      >
        <OTPInput
          value={otp}
          valueLength={otpLength}
          onChange={handleOtpChange}
          otpText={otpMsgText}
          error={otpError}
        />
      </LinearGradient>
      <PrimaryResendOTPLine />
      <SecondaryResendOTPLine />
      <LoadingModal loading={isLoading} />
      <OtpNumberModal
        visible={showNumberModal}
        onPress={resendOtp}
        onPressCancel={() => setShowNumberModal(false)}
        title="फ़ोन नंबर चुनें"
        phoneNumbers={phone_numbers}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  innerContainer: {
    margin: 16,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: color.black,
    paddingVertical: 24,
  },
  resendOtpContainer: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
  },
  resendOtpText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.blue,
    textDecorationLine: "underline",
  },
  phoneNumberText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.blue,
  },
  centeredContainer: {
    alignItems: "center",
  },
  rowCentered: {
    flexDirection: "row",
    justifyContent: "center",
  },
  mediumBlackText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.mediumBlacklightBlack,
  },
  resendActionText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.blue,
    textDecorationLine: "underline",
  },
  countdownText: {
    color: color.red,
  },
});

export default RescheduleOTPVerification;
